import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { apiClient } from '@/App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CreditCard, Check, Calendar, Zap, Shield, Clock, Lock, AlertCircle, Phone, Star, Sparkles } from 'lucide-react';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1e293b',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': { color: '#94a3b8' },
      iconColor: '#64748b',
    },
    invalid: { color: '#ef4444', iconColor: '#ef4444' },
  },
  hidePostalCode: true,
};

const CheckoutForm = ({ selectedPlan, coproprieteId, onSuccess, onCancel, plans, isPro }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  const plan = plans[selectedPlan];

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/payment/create-payment-intent', {
        copropriete_id: coproprieteId,
        plan: selectedPlan,
      });

      const { client_secret, payment_intent_id } = response.data;
      const cardElement = elements.getElement(CardElement);
      const result = await stripe.confirmCardPayment(client_secret, {
        payment_method: { card: cardElement },
      });

      if (result.error) {
        setError(result.error.message);
        setLoading(false);
      } else if (result.paymentIntent.status === 'succeeded') {
        await apiClient.post(`/payment/confirm-payment?payment_intent_id=${payment_intent_id}`);
        toast.success('Paiement réussi ! Votre abonnement est maintenant actif.');
        onSuccess();
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || 'Erreur lors du paiement');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className={`rounded-lg p-4 border ${isPro ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-slate-900">{plan?.name}</p>
            <p className="text-sm text-slate-500">
              {selectedPlan === 'annual' ? 'Facturation annuelle' : 'Facturation mensuelle'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-slate-900">
              {plan?.price}€{isPro && <span className="text-sm font-normal"> HT</span>}
            </p>
            <p className="text-sm text-slate-500">
              {selectedPlan === 'annual' ? '/an' : '/mois'}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-slate-700">
          Informations de carte bancaire
        </label>
        <div className="bg-white border border-slate-300 rounded-lg p-4 focus-within:border-emerald-500 focus-within:ring-1 focus-within:ring-emerald-500 transition-all">
          <CardElement options={CARD_ELEMENT_OPTIONS} onChange={(e) => setCardComplete(e.complete)} />
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Lock className="w-3 h-3" />
          <span>Paiement sécurisé par Stripe - Vos données sont chiffrées</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <Button
          type="submit"
          disabled={!stripe || loading || !cardComplete}
          className={`w-full py-6 text-lg ${isPro ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`}
          data-testid="confirm-payment-btn"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Paiement en cours...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Payer {plan?.price}€{isPro && ' HT'}
            </span>
          )}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading} className="w-full">
          Retour au choix du plan
        </Button>
      </div>
    </form>
  );
};

const PaymentModal = ({ open, onOpenChange, coproprieteId, onSubscriptionUpdate }) => {
  const [plans, setPlans] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [trialLoading, setTrialLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [supportPhone, setSupportPhone] = useState(null);

  useEffect(() => {
    if (open) {
      fetchPlans();
      fetchSubscriptionStatus();
      setShowCheckoutForm(false);
    }
  }, [open]);

  const fetchPlans = async () => {
    try {
      const response = await apiClient.get('/subscription/plans');
      setPlans(response.data.plans);
      setIsPro(response.data.is_pro || false);
      setSupportPhone(response.data.support_phone || null);
    } catch (error) {
      console.error('Erreur chargement plans:', error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await apiClient.get('/subscription/status');
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Erreur chargement statut:', error);
    }
  };

  const startTrial = async () => {
    setTrialLoading(true);
    try {
      const response = await apiClient.post('/subscription/start-trial');
      toast.success(response.data.message);
      onSubscriptionUpdate && onSubscriptionUpdate();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation de l\'essai');
    } finally {
      setTrialLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    onSubscriptionUpdate && onSubscriptionUpdate();
    onOpenChange(false);
  };

  const canStartTrial = !subscriptionStatus?.has_subscription;
  const isTrialActive = subscriptionStatus?.status === 'trial' && subscriptionStatus?.can_use_premium;
  const isExpired = subscriptionStatus?.status === 'expired';

  const monthlyPrice = plans?.monthly?.price || (isPro ? 179 : 49);
  const annualPrice = plans?.annual?.price || (isPro ? 1790 : 490);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            {isPro ? <Star className="w-6 h-6 text-blue-600" /> : <CreditCard className="w-6 h-6 text-emerald-600" />}
            {showCheckoutForm 
              ? 'Finaliser le paiement'
              : isPro 
                ? 'Offre Syndic Professionnel' 
                : isTrialActive 
                  ? 'Passez à l\'abonnement' 
                  : 'Débloquez toutes les fonctionnalités'}
          </DialogTitle>
          <DialogDescription>
            {showCheckoutForm
              ? 'Entrez vos informations de carte bancaire pour activer votre abonnement.'
              : isPro
                ? 'Accédez à toutes les fonctionnalités avec un support dédié.'
                : isTrialActive 
                  ? `Votre essai gratuit expire dans ${subscriptionStatus?.days_left} jours.`
                  : 'Générez des PDF, créez des convocations et bien plus encore.'}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {showCheckoutForm ? (
            <Elements stripe={stripePromise}>
              <CheckoutForm
                selectedPlan={selectedPlan}
                coproprieteId={coproprieteId}
                onSuccess={handlePaymentSuccess}
                onCancel={() => setShowCheckoutForm(false)}
                plans={plans}
                isPro={isPro}
              />
            </Elements>
          ) : (
            <>
              {/* Support Pro - Affiché AVANT paiement */}
              {isPro && supportPhone && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Phone className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-blue-900">Support dédié Syndic Pro</p>
                      <a href={`tel:${supportPhone.replace(/\s/g, '')}`} className="text-xl font-bold text-blue-600 hover:text-blue-700">
                        {supportPhone}
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Avantages */}
              <div className={`rounded-lg p-4 ${isPro ? 'bg-blue-50' : 'bg-slate-50'}`}>
                <h4 className="font-semibold text-slate-900 mb-3">
                  {isPro ? 'Avantages Syndic Pro' : 'Fonctionnalités Premium'}
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {isPro ? (
                    <>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-blue-500" />
                        Copropriétés illimitées
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Phone className="w-4 h-4 text-blue-500" />
                        Support ligne dédiée
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Sparkles className="w-4 h-4 text-blue-500" />
                        Beta en avant-première
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Star className="w-4 h-4 text-blue-500" />
                        Founding Care
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Zap className="w-4 h-4 text-emerald-500" />
                        Génération de PDF illimitée
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Calendar className="w-4 h-4 text-emerald-500" />
                        Convocations d'AG
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Shield className="w-4 h-4 text-emerald-500" />
                        Lettres de relance
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-emerald-500" />
                        Appels de fonds PDF
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Essai gratuit (uniquement pour les particuliers) */}
              {!isPro && canStartTrial && (
                <div className="border-2 border-dashed border-emerald-300 rounded-lg p-4 bg-emerald-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-emerald-800 flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        Essai gratuit de 7 jours
                      </h4>
                      <p className="text-sm text-emerald-600 mt-1">
                        Testez toutes les fonctionnalités sans engagement
                      </p>
                    </div>
                    <Button
                      onClick={startTrial}
                      disabled={trialLoading}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="start-trial-button"
                    >
                      {trialLoading ? 'Activation...' : 'Démarrer l\'essai'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Plans de tarification */}
              <div className="grid grid-cols-2 gap-4">
                {/* Plan Mensuel */}
                <div
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    selectedPlan === 'monthly'
                      ? isPro ? 'border-blue-600 bg-blue-50' : 'border-slate-900 bg-slate-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setSelectedPlan('monthly')}
                  data-testid="plan-monthly"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-slate-900">Mensuel</span>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === 'monthly' 
                        ? isPro ? 'border-blue-600 bg-blue-600' : 'border-slate-900 bg-slate-900' 
                        : 'border-slate-300'
                    }`}>
                      {selectedPlan === 'monthly' && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-slate-900">
                    {monthlyPrice}€{isPro && <span className="text-sm font-normal text-slate-500"> HT</span>}
                    <span className="text-base font-normal text-slate-500">/mois</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-2">
                    Facturation mensuelle, sans engagement
                  </p>
                </div>

                {/* Plan Annuel */}
                <div
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all relative ${
                    selectedPlan === 'annual'
                      ? isPro ? 'border-blue-600 bg-blue-50' : 'border-emerald-600 bg-emerald-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setSelectedPlan('annual')}
                  data-testid="plan-annual"
                >
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-white text-xs font-bold px-3 py-1 rounded-full ${
                    isPro ? 'bg-blue-600' : 'bg-emerald-600'
                  }`}>
                    2 MOIS OFFERTS
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-slate-900">Annuel</span>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === 'annual' 
                        ? isPro ? 'border-blue-600 bg-blue-600' : 'border-emerald-600 bg-emerald-600' 
                        : 'border-slate-300'
                    }`}>
                      {selectedPlan === 'annual' && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-slate-900">
                    {annualPrice}€{isPro && <span className="text-sm font-normal text-slate-500"> HT</span>}
                    <span className="text-base font-normal text-slate-500">/an</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-2">
                    {isPro 
                      ? `Soit ${Math.round(annualPrice/12)}€ HT/mois`
                      : `Soit ${Math.round(annualPrice/12)}€/mois - Économisez ${monthlyPrice * 12 - annualPrice}€`
                    }
                  </p>
                </div>
              </div>

              {/* Note */}
              <p className="text-sm text-slate-500 text-center">
                {isPro 
                  ? '* Tarif HT. Copropriétés illimitées incluses.'
                  : '* Tarif par copropriété. Le paiement couvre tous les copropriétaires.'}
              </p>

              {/* Founding Care (Pro uniquement) */}
              {isPro && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                  <h4 className="font-semibold text-blue-900 flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5" />
                    Founding Care
                  </h4>
                  <p className="text-sm text-blue-700">
                    En tant que membre fondateur, vos suggestions de fonctionnalités sont prioritaires. 
                    Nous sommes ouverts à vos idées !
                  </p>
                </div>
              )}

              {/* Bouton continuer */}
              <Button
                onClick={() => setShowCheckoutForm(true)}
                className={`w-full py-6 text-lg ${isPro ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-900 hover:bg-slate-800'} text-white`}
                data-testid="proceed-to-payment-btn"
              >
                <span className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Continuer - {selectedPlan === 'annual' ? annualPrice : monthlyPrice}€{isPro && ' HT'} ({selectedPlan === 'annual' ? 'Annuel' : 'Mensuel'})
                </span>
              </Button>

              {/* Garanties */}
              <div className="flex items-center justify-center gap-6 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Shield className="w-4 h-4" />
                  Paiement sécurisé
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  Annulation facile
                </span>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
